import React, { useContext, useLayoutEffect, useMemo, useState } from "react";

import * as router from "hassibot/internal-router";
import { get, set } from "hassibot/local-storage";
import { useSessionCollaboratorContext } from "startup/inside-components";

import { indexedDBCache } from "./services_v2/common/cache";
import { LegalEntity, LegalMode } from "./services_v2/common/types";

export const FORCE_SWITCH_LEGAL_ENTITY_PARAM = "force-switch-legal-entity";
export const CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY = "collaborator-current-legal-entity-uuid";
export const CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY = "collaborator-current-legal-mode";

const REDIRECTION_LIST_VIEWS = [
  router.proclientAppRouter.list(),
  router.caregiverAppRouter.broadcastList(),
  router.caregiverAppRouter.list(),
  router.prescriberAppRouter.structureList(),
  router.prescriberAppRouter.list(),
  router.picoAppRouter.list(),
  router.badgeAppRouter.badges(),
  router.monetAppRouter.listBills(),
  router.monetAppRouter.listRegularizations(),
  router.monetAppRouter.listPayments(),
  router.monetAppRouter.listProclientsMembersPaymentsIntents(),
  router.monetAppRouter.listCaregiversPaymentsIntents(),
  router.monetAppRouter.listTaxCreditPaymentRequests(),
  router.monetAppRouter.listThirdPartyPayers(),
  router.compassAppRouter.root(),
  router.metabaseAppRouter.root(),
];

interface CurrentLegalEntityContextType {
  currentLegalEntity: LegalEntity;
  setCurrentLegalEntity: (newLegalEntity: LegalEntity) => void;
}

const CurrentLegalEntityContext = React.createContext<CurrentLegalEntityContextType | undefined>(
  undefined
);

export const CurrentLegalEntityProvider = ({ children }) => {
  const { sessionCollaborator } = useSessionCollaboratorContext();

  const params = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);
  const paramsLegalEntityUuid = params.get(FORCE_SWITCH_LEGAL_ENTITY_PARAM);

  const { myAccessibleLegalEntities } = sessionCollaborator;

  const getDefaultLegalEntity = (): LegalEntity => {
    if (myAccessibleLegalEntities.length === 0) {
      throw new Error("No legal entity found for the current user");
    }

    const paramsLegalEntity = myAccessibleLegalEntities.find(
      le => le.uuid === paramsLegalEntityUuid
    );
    if (paramsLegalEntity) {
      return paramsLegalEntity;
    }

    // Get initial value from local storage or arbitrarily use the first value from the given list
    const legalEntityUuidFromLocalStorage = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const legalModeFromLocalStorage = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);
    const currentLegalEntity =
      myAccessibleLegalEntities.find(
        legalEntity =>
          legalEntityUuidFromLocalStorage === legalEntity.uuid &&
          legalModeFromLocalStorage === legalEntity.mode
      ) ?? myAccessibleLegalEntities[0];

    return currentLegalEntity;
  };

  const [legalEntity, setLegalEntity] = useState<LegalEntity>(getDefaultLegalEntity);

  useLayoutEffect(() => {
    indexedDBCache.initLegalEntityDatabase(legalEntity);
  }, [legalEntity]);

  useLayoutEffect(() => {
    const legalEntityUuidFromLocalStorage = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const legalModeFromLocalStorage = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);

    const legalEntityChanged =
      legalEntityUuidFromLocalStorage !== legalEntity.uuid ||
      legalModeFromLocalStorage !== legalEntity.mode;

    if (!legalEntityChanged) return;

    set(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY, legalEntity.uuid);
    set(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY, legalEntity.mode);

    // A full reload is required whenever we persist a selection, including the
    // very first login. `services_v2` captures the current legal entity/mode
    // from localStorage once at module import (used for the X-LEGAL-ENTITY-UUID
    // header and the mandatary/contractor branching). Those module values never
    // update afterwards, so on a fresh login (empty localStorage) they stay at
    // their boot value (null); reloading with localStorage now populated is the
    // only safe way to apply the selection and rebuild the per-entity state.
    if (paramsLegalEntityUuid) {
      params.delete(FORCE_SWITCH_LEGAL_ENTITY_PARAM);
      const newSearch = params.toString() ? `?${params.toString()}` : "";
      const redirect = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.location.href = redirect;
    } else {
      const path = window.location.pathname.split("?")[0];
      const closestListView = REDIRECTION_LIST_VIEWS.find(listView => {
        return path.startsWith(listView);
      });
      if (closestListView) window.location.href = closestListView;
      else window.location.reload();
    }
  }, [legalEntity, params, paramsLegalEntityUuid]);

  return (
    <CurrentLegalEntityContext.Provider
      value={{ currentLegalEntity: legalEntity, setCurrentLegalEntity: setLegalEntity }}
    >
      {children}
    </CurrentLegalEntityContext.Provider>
  );
};

export const useLegalMode = () => {
  const context = useContext(CurrentLegalEntityContext);

  if (!context) {
    throw new Error(
      "The application context is missing, add 'CurrentLegalEntityProvider' at the top of component hierarchy"
    );
  }

  const { currentLegalEntity, setCurrentLegalEntity } = context;

  const isMandatary = currentLegalEntity.mode === LegalMode.MANDATARY;
  const isContractor = currentLegalEntity.mode === LegalMode.CONTRACTOR;
  const isSandbox = currentLegalEntity.isSandbox;

  return {
    mode: currentLegalEntity.mode,
    isMandatary,
    isContractor,
    isSandbox,
    currentLegalEntity,
    setCurrentLegalEntity,
  };
};
